import { SHEFIRA_COMBAT_CLIPS } from '../cinematics/dragon-clips.js';

/**
 * Shefira walking on the ground of her Aire (ADR 0068): where her feet are
 * drawn while the server moves her tile by tile, and which clip her legs
 * play. Pure: fed where the scene draws her tile, read at the frame's time.
 *
 * The scene eases every entity toward its tile quickly and then waits for
 * the next step, which reads as a glide in fits for a body her size. Her
 * feet follow that point instead at her own walking pace (her tiles per
 * second), a little faster only when they fall behind, so she treads
 * steadily across the ground while her stride plays. The stride is drawn
 * with her wings folded: `fold` folds them as she sets off and, played
 * backwards, spreads them again once she has stopped, back to the idle pose
 * every other clip starts from.
 */

export interface Point {
  x: number;
  y: number;
}

const TILE = 16;
/** Further than this from where she is drawn, she is put there at once (as the scene snaps). */
const SNAP_PX = 4 * TILE;
/** A pause shorter than this between two steps keeps her walking. */
const STRIDE_GRACE_MS = 200;
const MAX_FRAME_MS = 100;
const FOLD = SHEFIRA_COMBAT_CLIPS.fold;
const FOLD_MS = FOLD.frames * FOLD.ms;
const WALK = SHEFIRA_COMBAT_CLIPS.walk;

export type GaitClip = 'fold' | 'walk';

type Wings =
  | { kind: 'spread' }
  | { kind: 'folding'; at: number }
  | { kind: 'folded'; since: number }
  | { kind: 'spreading'; at: number };

export class ShefiraGait {
  private at: Point | undefined;
  private lastAt = 0;
  private movedAt = Number.NEGATIVE_INFINITY;
  private wings: Wings = { kind: 'spread' };
  /** Her pace in world pixels per millisecond. */
  private readonly pace: number;

  constructor(tilesPerSecond: number) {
    this.pace = (tilesPerSecond * TILE) / 1000;
  }

  /** Where her feet are drawn now, unrounded; undefined before the first `follow`. */
  get feet(): Point | undefined {
    return this.at === undefined ? undefined : { ...this.at };
  }

  /** Put her there at once (she landed, or came into view): no walk to it. */
  place(target: Point, now: number): void {
    this.at = { ...target };
    this.lastAt = now;
  }

  /**
   * One frame: her feet step toward `target` (where the scene draws her
   * tile) at her pace, up to twice as fast while more than a tile behind.
   */
  follow(target: Point, now: number): Point {
    const at = this.at;
    // A frame long after the last (a flight, a hidden tab) is not a stride's worth of ground.
    const dt = Math.max(0, Math.min(MAX_FRAME_MS, now - this.lastAt));
    this.lastAt = now;
    if (at === undefined) {
      this.at = { ...target };
      return { ...target };
    }
    const dx = target.x - at.x;
    const dy = target.y - at.y;
    const distance = Math.hypot(dx, dy);
    if (distance > SNAP_PX) {
      this.at = { ...target };
      return { ...target };
    }
    if (distance > 0.01) {
      const behind = Math.max(0, Math.min(1, (distance - TILE) / TILE));
      const step = Math.min(distance, this.pace * (1 + behind) * dt);
      at.x += (dx / distance) * step;
      at.y += (dy / distance) * step;
      if (step > 0) this.movedAt = now;
    }
    return { ...at };
  }

  /** Whether her feet are moving, a short pause between steps included. */
  walking(now: number): boolean {
    return now - this.movedAt < STRIDE_GRACE_MS;
  }

  /**
   * Her legs' clip and frame at `now`, or null when she stands with her
   * wings spread (the caller shows her idle or her attack). Advances the
   * wings' folding: call once per drawn frame.
   */
  clip(now: number): { clip: GaitClip; frame: number } | null {
    const walking = this.walking(now);
    let wings = this.wings;
    if (wings.kind === 'folding' && now - wings.at >= FOLD_MS) {
      wings = { kind: 'folded', since: wings.at + FOLD_MS };
    }
    if (wings.kind === 'spreading' && now - wings.at >= FOLD_MS) wings = { kind: 'spread' };
    if (walking && wings.kind === 'spread') wings = { kind: 'folding', at: now };
    if (walking && wings.kind === 'spreading') {
      // Turned back halfway: fold again from the frame she had reached.
      wings = { kind: 'folding', at: now - (FOLD_MS - (now - wings.at)) };
    }
    if (!walking && wings.kind === 'folded') wings = { kind: 'spreading', at: now };
    if (!walking && wings.kind === 'folding') {
      wings = { kind: 'spreading', at: now - (FOLD_MS - (now - wings.at)) };
    }
    this.wings = wings;
    switch (wings.kind) {
      case 'spread':
        return null;
      case 'folding':
        return {
          clip: 'fold',
          frame: Math.min(FOLD.frames - 1, Math.floor((now - wings.at) / FOLD.ms)),
        };
      case 'spreading':
        return {
          clip: 'fold',
          frame: Math.max(0, FOLD.frames - 1 - Math.floor((now - wings.at) / FOLD.ms)),
        };
      case 'folded':
        return {
          clip: 'walk',
          frame: Math.floor(Math.max(0, now - wings.since) / WALK.ms) % WALK.frames,
        };
    }
  }

  /** An attack, a flight: her wings are spread again at once, as every such clip begins. */
  spread(): void {
    this.wings = { kind: 'spread' };
  }
}
